import { EffectRef, Injectable, Injector, WritableSignal, computed, effect, inject, signal, untracked } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { ChatBotScriptService } from './chatbot.service';
import { GoogleTranslateService } from './google-translate.service';
import { baseLanguageCode, normalizeLanguageCode, resolveLanguageCode } from './helpers/language.util';
import { LocalStorageService } from './localStorage.service';
import { RestService } from './rest.service';

const LANG_STORAGE_KEY = 'selectedLang';
const LANG_COOKIE_KEY = 'locallang';

// Locales regionales que app.module registra en Angular (mantener en sincronía
// con sus registerLocaleData). Cualquier otro código regional degrada al idioma
// base para las fechas en vez de reventar el date pipe.
const REGISTERED_REGIONAL_LOCALES = new Set(['pt-PT', 'pt-BR', 'es-AR']);

// es → es-ES, va → ca-ES-valencia... (códigos que consumen los datepickers).
// Los idiomas regionales del CMS (pt-BR, es-AR…) ya SON un locale
// completo y no pasan por aquí — ver selectedLocaleCode (HRS-2934).
const LOCALE_BY_LANG: Record<string, string> = {
  es: 'es-ES',
  en: 'en-GB',
  fr: 'fr-FR',
  it: 'it-IT',
  de: 'de-DE',
  ch: 'ch-CH',
  ca: 'ca-ES',
  nl: 'nl-NL',
  gl: 'gl-ES',
  va: 'ca-ES-valencia',
  pt: 'pt-PT',
  eu: 'eu-ES',
  ru: 'ru-RU',
  ja: 'ja-JP',
  ko: 'ko-KR',
};

/**
 * Fachada única de idioma: los componentes no inyectan TranslateService (ngx-translate)
 * ni GoogleTranslateService, todo pasa por aquí.
 *
 * Dos mundos según el idioma pedido:
 *  - Idiomas del tenant (device().languages): diccionario ngx-translate, sin reload.
 *  - Resto de idiomas soportados por Google: cookie googtrans + reload para que el
 *    widget de Google traduzca el DOM.
 */
@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private _translate = inject(TranslateService);
  private _google = inject(GoogleTranslateService);
  private _chatbot = inject(ChatBotScriptService);
  private _localStorage = inject(LocalStorageService);
  private _cookieService = inject(CookieService);
  private _rest = inject(RestService);

  /** Idioma de diccionario/datos (Accept-Language, campos multiidioma de la API) */
  private _selectedLanguage: WritableSignal<string> = signal('es');
  public selectedLanguage = this._selectedLanguage.asReadonly();

  /** Target de la cookie googtrans si hay traducción automática activa */
  private _googleLang = signal<string | null>(null);
  public readonly isGoogleTranslated = computed(() => this._googleLang() !== null);
  /** Idioma que VE el usuario: el de Google si su cookie está activa, si no el de diccionario */
  public readonly displayLanguage = computed(() => this._googleLang() ?? this._selectedLanguage());

  /** Idiomas con diccionario habilitados para el tenant (o los propios de una encuesta/canal) */
  private _availableLanguages = signal<string[]>([]);
  public availableLanguages = this._availableLanguages.asReadonly();
  /** Lista {code, img} para las banderas del selector del header */
  public readonly languages = computed(() =>
    this._availableLanguages().map((code) => ({ code, img: `/assets/langs/${code}.svg` }))
  );
  /**
   * Idiomas de Google no cubiertos por diccionario del tenant (modal de Google).
   * Se compara por idioma base (HRS-2934): un tenant con pt-BR ya cubre
   * el portugués, así que el 'pt' de Google no debe ofrecerse como duplicado
   * que acabaría en traducción automática teniendo diccionario propio.
   */
  public readonly googleLanguages = computed(() => {
    const tenantBases = new Set(this._availableLanguages().map((code) => baseLanguageCode(code)));
    return this._google.availableLangs.filter((lang) => !tenantBases.has(baseLanguageCode(lang.code)));
  });
  /** Catálogo completo de idiomas de Google (widget translate_a/element.js) */
  public readonly allGoogleLanguages = this._google.availableLangs;

  private _headerLangType = signal('flags');
  public headerLangType = this._headerLangType.asReadonly();
  private _showGoogleLangs = signal(true);
  public showGoogleLangs = this._showGoogleLangs.asReadonly();

  /**
   * Locale para el date pipe ('va' no existe como locale: las fechas se muestran
   * en catalán). Los códigos regionales usan su propio locale si app.module lo
   * registra (REGISTERED_REGIONAL_LOCALES) y, si no, degradan al idioma base:
   * el date pipe LANZA con un locale sin registrar (HRS-2934).
   */
  public readonly dateLocale = computed(() => {
    const lang = this._selectedLanguage();
    if (lang === 'va')
      return 'ca';
    if (!lang.includes('-'))
      return lang;
    return REGISTERED_REGIONAL_LOCALES.has(lang) ? lang : baseLanguageCode(lang);
  });
  /**
   * es → es-ES, en → en-GB... para los datepickers. Un código regional del CMS
   * (pt-BR, es-AR) ya es un locale completo y se usa tal cual (HRS-2934).
   */
  public readonly selectedLocaleCode = computed(() => {
    const lang = this._selectedLanguage();
    if (lang.includes('-'))
      return lang;
    return LOCALE_BY_LANG[lang] ?? LOCALE_BY_LANG['es'];
  });

  private _syncHtmlLang = effect(() => {
    document.documentElement.lang = this.displayLanguage();
  });

  constructor() {
    this._translate.setDefaultLang('es');
    this._translate.use('es');
    this._googleLang.set(this._google.getCookieLang());
  }

  /**
   * Orquesta el idioma de arranque una vez resuelto el device del tenant.
   * Prioridad: ?lang= → persistido (localStorage/cookie) → mainLanguage → navegador → 'es'.
   * Lee rest.device() en el momento de la llamada: el mainLanguage no puede capturarse
   * en la construcción del servicio porque el device aún no está resuelto.
   */
  public initFromDevice(queryParamLang?: string): void {
    const device = this._rest.device();
    if (!device?.showGoogleLangs)
      this._showGoogleLangs.set(false);

    this._headerLangType.set(device?.headerLangType ?? 'flags');
    this._availableLanguages.set(device?.languages ?? []);

    const mainLang = device?.mainLanguage || this.getBrowserLang() || 'es';
    const desired = queryParamLang || this._readPersistedLang() || mainLang;
    this._applyInitialLang(desired, device?.mainLanguage || 'es');
  }

  /**
   * Camino ÚNICO de cambio de idioma por acción del usuario (selector del header,
   * modal de Google, ruta /:lang). Decide diccionario vs Google y hace como mucho
   * UN reload, persistiendo SIEMPRE antes de recargar.
   */
  public changeLanguage(lang: string): void {
    if (!lang || lang === this.displayLanguage())
      return;

    const cookieLang = this._google.getCookieLang();

    // Resolución regional (HRS-2934): un 'pt' persistido de antes de la
    // migración, o un enlace /:lang antiguo, tiene que encajar con el
    // pt-BR del tenant. Con el match exacto de antes caía a la rama de Google y
    // el usuario acababa con traducción automática teniendo diccionario propio.
    const tenantLang = resolveLanguageCode(lang, this._tenantLangs());
    if (tenantLang) {
      if (cookieLang) {
        // google → diccionario: al arrancar tras el reload, lo persistido resuelve el idioma
        this._persistLang(tenantLang);
        this._google.clearCookie();
        this._google.reloadPage();
        return;
      }
      this.useDictionaryLang(tenantLang);
      return;
    }

    if (!this._google.isSupported(lang))
      return;

    // diccionario → google | google → google
    this._persistLang(lang);
    if (cookieLang)
      this._google.clearCookie();
    this._google.setCookie(this._mainLanguage(), lang);
    this._google.reloadPage();
  }

  /**
   * Aplica un idioma de diccionario (ngx-translate) sin reload ni cookie googtrans.
   * persist:false para contextos donde la elección no debe capturar la preferencia
   * global del usuario (embed, encuestas, rutas standalone).
   */
  public useDictionaryLang(lang: string, opts?: { persist?: boolean }): void {
    // Normaliza el casing (HRS-2934): hay llamadores que hacen toLowerCase()
    // (load-from-api, embed) y con 'pt-pt' el loader pediría pt-pt.php (404 en
    // el CMS, case-sensitive) y dateLocale no reconocería el locale registrado.
    lang = normalizeLanguageCode(lang);
    if (!lang)
      return;

    this._translate.setDefaultLang(lang);
    this._translate.use(lang);
    this._selectedLanguage.set(lang);
    this._chatbot.setLang(lang);
    if (opts?.persist ?? true)
      this._persistLang(lang);
  }

  /** Wrapper de TranslateService.instant: única vía para traducir claves desde TS */
  public instant(key: string, interpolateParams?: Record<string, unknown>): string {
    return this._translate.instant(key, interpolateParams);
  }

  /**
   * Idioma del navegador, resuelto primero contra los idiomas del tenant con
   * fallback regional (HRS-2934): un navegador en pt-BR encaja con el pt-BR del
   * cliente, y uno en pt con su variante. Antes se truncaba a 2 letras y se
   * validaba solo contra Google, así que un tenant con idioma regional acababa
   * en Google Translate teniendo diccionario propio.
   */
  public getBrowserLang(): string | null {
    if (!navigator.language)
      return null;

    const tenantMatch = resolveLanguageCode(navigator.language, this._tenantLangs());
    if (tenantMatch)
      return tenantMatch;

    const langCode = baseLanguageCode(navigator.language);
    return this._google.isSupported(langCode) ? langCode : null;
  }

  /** Sobrescribe los idiomas de diccionario (encuestas con langs propios, registro por canal) */
  public setTenantLanguages(langs: string[] | undefined | null): void {
    this._availableLanguages.set(langs ?? []);
  }

  /**
   * Ejecuta el callback cuando CAMBIA el idioma de diccionario (no en el registro
   * inicial), con la misma semántica que translate.onLangChange. Ligado al ciclo
   * de vida del contexto de inyección desde el que se llama (o del injector dado).
   */
  public onLanguageChange(callback: (lang: string) => void, options?: { injector?: Injector }): EffectRef {
    let previousLang = untracked(() => this.selectedLanguage());
    return effect(() => {
      const lang = this.selectedLanguage();
      if (lang === previousLang)
        return;

      previousLang = lang;
      untracked(() => callback(lang));
    }, { injector: options?.injector });
  }

  private _applyInitialLang(lang: string, mainLang: string): void {
    if (!lang)
      return;

    const cookieLang = this._google.getCookieLang();

    // Misma resolución regional que changeLanguage (HRS-2934): el 'pt'
    // persistido de un usuario recurrente tiene que resolver al pt-BR
    // del tenant en vez de arrancar con traducción automática de Google.
    const tenantLang = resolveLanguageCode(lang, this._tenantLangs());
    if (tenantLang) {
      if (cookieLang) {
        // Traducción de Google activa pero el idioma deseado tiene diccionario:
        // persistir ANTES del reload para que el próximo arranque lo resuelva.
        this._persistLang(tenantLang);
        this._google.clearCookie();
        this._google.reloadPage();
        return;
      }
      this.useDictionaryLang(tenantLang);
      return;
    }

    if (cookieLang === lang) {
      // La cookie ya traduce a ese idioma: cargar el diccionario del idioma
      // principal por debajo de la traducción de Google.
      this._googleLang.set(lang);
      this.useDictionaryLang(mainLang, { persist: false });
      return;
    }

    if (!this._google.isSupported(lang)) {
      this.useDictionaryLang(mainLang, { persist: false });
      return;
    }

    // Idioma de Google distinto al de la cookie actual
    this._persistLang(lang);
    this._google.clearCookie();
    this._google.setCookie(mainLang, lang);
    this._google.reloadPage();
  }

  private _tenantLangs(): string[] {
    const langs = this._availableLanguages();
    return langs.length ? langs : this._rest.device()?.languages ?? [];
  }

  private _mainLanguage(): string {
    return this._rest.device()?.mainLanguage || 'es';
  }

  private _persistLang(lang: string): void {
    this._localStorage.setItem(LANG_STORAGE_KEY, lang);
    // Espejo en cookie para sesiones antiguas y lectores fuera de Angular (TTS del chatbot)
    this._cookieService.delete(LANG_COOKIE_KEY);
    this._cookieService.set(LANG_COOKIE_KEY, lang, undefined, '/', undefined, true, 'Lax', false);
  }

  private _readPersistedLang(): string | null {
    const stored = this._localStorage.getItem<string>(LANG_STORAGE_KEY);
    if (stored)
      return stored;

    // Migración: las sesiones previas solo tienen la cookie locallang
    const cookieLang = this._cookieService.get(LANG_COOKIE_KEY);
    if (cookieLang) {
      this._localStorage.setItem(LANG_STORAGE_KEY, cookieLang);
      return cookieLang;
    }

    return null;
  }
}
